import React, { useEffect } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  actionSaveStepPickAPlan, actionClearPromoCodeStatus, actionSaveIsCharity, actionApplyPromoCode, actionApplyReferralCode,
} from 'store/modules/sign-up/actions';
import { IReducerState, history } from 'store/main-reducer';
import {
  getWorkItOutData, getChosenExtras, getChosenPlan, getIsCharity, getPromoCodeIsLoading, getPromoCodeIsSuccess, getPromoCodeIsFailure, getPromoCodeErrorMessage, getDefaultPromoCode, getPromoCodeDiscount, getReferralCode,
} from 'store/modules/sign-up/selectors';

import {
  IPlan, IWorkItOutData, Bank, PromoCodeDiscount, IExtra,
} from 'types';
import {
  getHasLoadedOptions, getPlans, getExtras, getBankOptions,
} from 'store/modules/options/selectors';
import { CallbackRequestModal } from 'views/components/CallbackRequestModal/CallbackRequestModal';
import { getIsViewportSmall } from 'store/modules/app/selectors';
import { HR_WE_DO_THIS_OURSELVES } from 'store/modules/app/constants';
import { PickAPlanView } from './PickAPlan';

interface ActionProps {
  saveStepPickAPlan: typeof actionSaveStepPickAPlan;
  clearPromoCodeStatus: typeof actionClearPromoCodeStatus;
  applyPromoCode: typeof actionApplyPromoCode,
  applyReferralCode: typeof actionApplyReferralCode,
  saveIsCharity: typeof actionSaveIsCharity,
}

interface ValueProps {
  chosenPlanId: string;
  chosenExtras: string[];

  hasLoadedOptions: boolean;
  plans: IPlan[];
  extras: IExtra[];
  workItOutData: IWorkItOutData;
  bankOptions: Bank[];
  isViewportSmall: boolean;

  isCharity: boolean;

  defaultPromoCode?: string;
  promoCodeDiscount?: PromoCodeDiscount;
  promoCodeIsLoading: boolean;
  promoCodeIsSuccess: boolean;
  promoCodeIsFailure: boolean;
  promoCodeErrorMessage?: string;

  referralCode?: string;
}

interface Props extends ValueProps, ActionProps {
  intl: IntlShape;
}

const PickAPlanController = (props: Props) => {
  const {
    intl,
    hasLoadedOptions,
    plans,
    extras,
    chosenExtras,
    chosenPlanId,
    saveStepPickAPlan,
    workItOutData,
    bankOptions,
    isViewportSmall,
    clearPromoCodeStatus,
    saveIsCharity,
    applyPromoCode,
    promoCodeIsLoading,
    promoCodeIsSuccess,
    promoCodeIsFailure,
    promoCodeErrorMessage,
    isCharity,
    defaultPromoCode,
    promoCodeDiscount,
    referralCode,
    applyReferralCode,
  } = props;

  useEffect(() => {
    if (defaultPromoCode) {
      applyPromoCode(defaultPromoCode);
    }
  }, [defaultPromoCode, applyPromoCode]);

  const goToNextStep = () => {
    history.push('/sign-up/contact-details');
    clearPromoCodeStatus();
  };

  const goToPreviousStep = () => {
    history.push('/sign-up/work-it-out');
  };

  if (!hasLoadedOptions) {
    history.push('/');
  }

  // Get the bank choice from the previous step to determine what plans should be enabled
  const { bankChoice, manageHr, managePayCycles } = workItOutData;
  // console.log('🤔🤔🤔 manageHr', manageHr);
  const recommendSmartHr = manageHr === HR_WE_DO_THIS_OURSELVES;

  const creditCheckedBanks = ['ANZ', 'ASB', 'BNZ', 'Westpac'];

  const isSmartPayrollEnabled = bankOptions.find(({ name }) => bankChoice?.name === name)?.smartPayroll || false;
  const creditCheckWarningEnabled = !creditCheckedBanks.find((name: string) => bankChoice?.name === name);
  const isFullService = managePayCycles === 'full-service';

  const extrasWithRequirements = extras.map((extra) => {
    if (recommendSmartHr) {
      return {
        ...extra,
        isRecommended: true,
      };
    }

    return extra;
  });

  let defaultPlanId = '';

  const plansWithRequirements = plans.map((plan) => {
    const isCalculateAndAutomatedPlan = plan.id === 'calculate-and-automate';
    const calculateOnlyPlan = plan.id === 'calculate-only';

    // Full Service (I'd like everything automated)
    if (isFullService) {
      if (isSmartPayrollEnabled) {
        if (isCalculateAndAutomatedPlan) {
          // isSelected: true,
          defaultPlanId = 'calculate-and-automate';
          return {
            ...plan,
            isRecommended: true,
          };
        }

        if (calculateOnlyPlan) {
          return plan;
        }
      } else {
        if (isCalculateAndAutomatedPlan) {
          // isSelected: true,
          defaultPlanId = 'calculate-and-automate';
          return {
            ...plan,
            isDisabled: true,
          };
        }

        if (calculateOnlyPlan) {
          // isSelected: true,
          defaultPlanId = 'calculate-only';
          return {
            ...plan,
            isRecommended: true,
          };
        }
      }
    } else {
      // Calc Only (I'd like to do this myself)
      if (isSmartPayrollEnabled) {
        if (isCalculateAndAutomatedPlan) {
          return {
            ...plan,
          };
        }

        if (calculateOnlyPlan) {
          // isSelected: true
          defaultPlanId = 'calculate-only';
          return {
            ...plan,
            isRecommended: true,
          };
        }
      }

      if (isCalculateAndAutomatedPlan) {
        return {
          ...plan,
          isDisabled: true,
        };
      }

      if (calculateOnlyPlan) {
        // isSelected: true,
        defaultPlanId = 'calculate-only';
        return {
          ...plan,
          isRecommended: true,
        };
      }
    }

    return plan;
  });

  const sortedPlans = plansWithRequirements.sort((a, b) => {
    const aVal = a.isRecommended ? 1 : 0;
    const bVal = b.isRecommended ? 1 : 0;
    return bVal - aVal;
  });

  // Set defaults
  // @TODO Could add hasVisited boolean to pickAPlan reducer state so we don't reset the user's choice if they go back and
  // forward between the first two steps
  useEffect(() => {
    if (!chosenPlanId && defaultPlanId !== chosenPlanId) {
      saveStepPickAPlan({
        chosenPlan: defaultPlanId,
        extras: [],
      });
    }
  }, [defaultPlanId, chosenPlanId, saveStepPickAPlan]);

  // =========================
  // Actions
  // =========================
  const onApplyCode = (promoCodeToApply: string) => {
    applyPromoCode(promoCodeToApply);
  };

  const onApplyReferralCode = (newReferralCode: string) => {
    applyReferralCode(newReferralCode);
  };

  return (
    <>
      <PickAPlanView
        intl={intl}
        chosenPlanId={chosenPlanId}
        chosenExtras={chosenExtras}
        plans={sortedPlans}
        creditCheckWarningEnabled={creditCheckWarningEnabled}
        extras={extrasWithRequirements}
        goToNextStep={goToNextStep}
        goToPreviousStep={goToPreviousStep}
        saveStepPickAPlan={saveStepPickAPlan}
        isViewportSmall={isViewportSmall}

        isCharity={isCharity}

        defaultPromoCode={defaultPromoCode}
        promoCodeDiscount={promoCodeDiscount}
        promoCodeIsLoading={promoCodeIsLoading}
        promoCodeIsSuccess={promoCodeIsSuccess}
        promoCodeIsFailure={promoCodeIsFailure}
        promoCodeErrorMessage={promoCodeErrorMessage || intl.formatMessage({ id: 'PROMO_CODE_SOMETHING_WENT_WRONG' })}

        referralCode={referralCode}

        setIsCharity={saveIsCharity}
        onApplyCode={onApplyCode}
        onApplyReferralCode={onApplyReferralCode}
      />
      <CallbackRequestModal />
    </>
  );
};

const mapStateToProps = (state: IReducerState): ValueProps => ({
  chosenPlanId: getChosenPlan(state),
  chosenExtras: getChosenExtras(state),
  hasLoadedOptions: getHasLoadedOptions(state),
  plans: getPlans(state),
  extras: getExtras(state),
  workItOutData: getWorkItOutData(state),
  bankOptions: getBankOptions(state),
  isViewportSmall: getIsViewportSmall(state),
  isCharity: getIsCharity(state),
  promoCodeDiscount: getPromoCodeDiscount(state),
  promoCodeIsLoading: getPromoCodeIsLoading(state),
  promoCodeIsSuccess: getPromoCodeIsSuccess(state),
  promoCodeIsFailure: getPromoCodeIsFailure(state),
  promoCodeErrorMessage: getPromoCodeErrorMessage(state),
  referralCode: getReferralCode(state),
  defaultPromoCode: getDefaultPromoCode(state),
});

const mapDispatchToProps: ActionProps = {
  saveStepPickAPlan: actionSaveStepPickAPlan,
  clearPromoCodeStatus: actionClearPromoCodeStatus,
  applyPromoCode: actionApplyPromoCode,
  applyReferralCode: actionApplyReferralCode,
  saveIsCharity: actionSaveIsCharity,

};

// Can't use redux compose here as the types are not happy
export const PickAPlan = injectIntl(connect(
  mapStateToProps,
  mapDispatchToProps,
)(PickAPlanController));
